import { iconSizes } from '@mds/tokens';
import { tableCellClasses, tableRowClasses, type Components } from '@mui/material';
import { themeVars } from '../themeVars';
import { typographyStyles, type Theme } from './utils';

export const MuiTableContainer: Components<Theme>['MuiTableContainer'] = {
  styleOverrides: {
    root: () => ({}),
  },
};

export const MuiTableHead: Components<Theme>['MuiTableHead'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundColor: themeVars(theme).palette.background.level1,
      borderBottom: `1px solid ${themeVars(theme).palette.border.subtle}`,
      [`& .${tableCellClasses.root}`]: {
        color: themeVars(theme).palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: theme.typography.overline.letterSpacing,
      },
    }),
  },
};

export const MuiTableRow: Components<Theme>['MuiTableRow'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      '&:nth-of-type(even)': {
        background: themeVars(theme).palette.background.level1,
      },
      [`&.${tableRowClasses.hover}:hover`]: {
        background: themeVars(theme).palette.action.hover,
        ...theme.applyStyles('dark', {
          background: themeVars(theme).palette.action.hover,
        }),
      },
      [`&.${tableRowClasses.selected}`]: {
        background: 'transparent',
        '&:nth-of-type(even)': {
          background: themeVars(theme).palette.background.level1,
        },
        '&:hover': {
          background: themeVars(theme).palette.action.hover,
          ...theme.applyStyles('dark', {
            background: themeVars(theme).palette.action.hover,
          }),
        },
      },
    }),
  },
};

export const MuiTableCell: Components<Theme>['MuiTableCell'] = {
  styleOverrides: {
    root: () => ({
      borderBottom: 0,
    }),
  },
  variants: [
    {
      props: {
        size: 'small',
      },
      style: ({ theme }) => ({
        ...typographyStyles(theme.typography.caption),
        padding: theme.spacing(1, 1),
        [`&.${tableCellClasses.paddingCheckbox}`]: {
          width: 24, // prevent the checkbox column from growing
          padding: '0 12px 0 16px',
          '& > *': {
            padding: 0,
          },
        },
      }),
    },
    {
      props: {
        size: 'medium',
      },
      style: ({ theme }) => ({
        ...typographyStyles(theme.typography.body2),
        padding: theme.spacing(1.25, 1),
        [`&.${tableCellClasses.paddingCheckbox}`]: {
          width: 24, // prevent the checkbox column from growing
          padding: '0 12px 0 16px',
          '& > *': {
            padding: 0,
          },
        },
      }),
    },
    {
      props: {
        size: 'large',
      },
      style: ({ theme }) => ({
        ...typographyStyles(theme.typography.body1),
        padding: theme.spacing(1, 1),
        [`&.${tableCellClasses.paddingCheckbox}`]: {
          width: 24, // prevent the checkbox column from growing
          padding: '0 12px 0 16px',
          '& > *': {
            padding: 0,
          },
        },
      }),
    },
  ],
};

export const MuiTablePagination: Components<Theme>['MuiTablePagination'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderTop: `1px solid ${themeVars(theme).palette.border.subtle}`,
      userSelect: 'none',
      background: themeVars(theme).palette.background.level1,
    }),
    toolbar: ({ theme }) => ({
      minHeight: 34,
      paddingInline: theme.spacing(1),
      [`${theme.breakpoints.up('xs')} and (orientation: landscape)`]: {
        minHeight: 34,
      },
      [theme.breakpoints.up('sm')]: {
        minHeight: 34,
        paddingInline: theme.spacing(1),
      },
    }),
    selectLabel: ({ theme }) => ({
      ...typographyStyles(theme.typography.label3),
      color: themeVars(theme).palette.text.secondary,
      marginBlock: 0,
    }),
    selectRoot: ({ theme }) => ({
      marginRight: 'auto',
      paddingRight: theme.spacing(1),
      '& svg': {
        '--size': `${iconSizes.sm}px`,
        color: themeVars(theme).palette.text.secondary,
      },
    }),
    select: ({ theme }) => ({
      ...typographyStyles(theme.typography.label3),
      color: themeVars(theme).palette.text.secondary,
      paddingBlock: 0,
    }),
    displayedRows: ({ theme }) => ({
      ...typographyStyles(theme.typography.label3),
      color: themeVars(theme).palette.text.secondary,
      marginBlock: 0,
    }),
    spacer: {
      flex: 0,
    },
    actions: {
      button: {
        marginBottom: 1,
        '& svg': {
          fontSize: iconSizes.xs,
        },
      },
    },
  },
};
